import React, { useState, useEffect } from 'react';
import { User, Bell, MapPin, Shield, Palette, Trash2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

import Header from '../../components/ui/Header';


import PersonalInfo from './components/PersonalInfo';
import NotificationSettings from './components/NotificationSettings';
import LocationSettings from './components/LocationSettings';
import PrivacySettings from './components/PrivacySettings';
import AppearanceSettings from './components/AppearanceSettings';
import AccountManagement from './components/AccountManagement';
import SubscriptionManagement from './components/SubscriptionManagement';
import Icon from '../../components/AppIcon';


const UserProfileSettings = () => {
  const { user, userProfile, loading: authLoading, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = '/user-login';
    }
  }, [user, authLoading]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleSave = async (updates) => {
    setSaving(true);
    try {
      const { error } = await updateProfile(updates);
      if (error) throw error;
      showMessage('success', 'Settings saved successfully');
    } catch (err) {
      console.error('Error saving settings:', err);
      showMessage('error', 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const tabs = [
    { id: 'personal', label: 'Personal Info', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'location', label: 'Location', icon: MapPin },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'subscriptions', label: 'Subscriptions', icon: Bell },
    { id: 'account', label: 'Account', icon: Trash2 }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Account Settings</h1>
          <p className="text-gray-600 mt-2">
            Manage your account preferences and notification settings
          </p>
        </div>

        {/* Message Display */}
        {message?.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center ${
            message?.type === 'success' ?'bg-green-50 text-green-800 border border-green-200' :'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <AlertCircle className="h-5 w-5 mr-2" />
            {message?.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-2">
              <nav className="space-y-1">
                {tabs?.map((tab) => {
                  const Icon = tab?.icon;
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                        activeTab === tab?.id
                          ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`}
                    >
                      <Icon className="h-5 w-5 mr-3" />
                      {tab?.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm">
              {activeTab === 'personal' && (
                <PersonalInfo 
                  user={user} 
                  userProfile={userProfile} 
                  onSave={handleSave} 
                  saving={saving} 
                />
              )}
              
              {activeTab === 'notifications' && (
                <NotificationSettings 
                  userProfile={userProfile} 
                  onSave={handleSave} 
                  saving={saving} 
                />
              )}
              
              {activeTab === 'location' && (
                <LocationSettings 
                  userProfile={userProfile} 
                  onSave={handleSave} 
                  saving={saving} 
                />
              )}
              
              {activeTab === 'privacy' && (
                <PrivacySettings 
                  userProfile={userProfile} 
                  onSave={handleSave} 
                  saving={saving} 
                />
              )}
              
              {activeTab === 'appearance' && (
                <AppearanceSettings 
                  userProfile={userProfile} 
                  onSave={handleSave} 
                  saving={saving} 
                />
              )}
              
              {activeTab === 'subscriptions' && (
                <SubscriptionManagement 
                  userId={user?.id} 
                />
              )}
              
              {activeTab === 'account' && (
                <AccountManagement 
                  user={user} 
                  userProfile={userProfile} 
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileSettings;